import React, { useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Mail } from "lucide-react";
import jobLogo from "../images/Logo.svg";

const ForgotPassword = () => {
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

  const validationSchema = Yup.object({
    email: Yup.string().email("Invalid email format").required("Email is required"),
  });

  const handleForgotPassword = async (values, { setSubmitting }) => {
    try {
      setError("");
      setMessage("");
      const response = await axios.post(`${REACT_APP_API_URL}/api/auth/forgotPasswordEmail`, values);

      if (response.data?.message) {
        setMessage(response.data.message);
      } else {
        setMessage("Password reset link sent. Please check your email.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Something went wrong. Try again.");
    }

    setSubmitting(false);
  };

  return (
    <div
      className="min-h-screen w-full font-['Inter',sans-serif] flex items-center justify-center p-4 md:p-8 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}
    >
      {/* Decorative background shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.3) 0%, transparent 70%)' }}
        />
        <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full opacity-20"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)' }}
        />
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full opacity-10"
          style={{ background: 'radial-gradient(circle, rgba(255,255,255,0.4) 0%, transparent 70%)' }}
        />
      </div>

      {/* Card */}
      <div className="relative w-full !max-w-lg bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl p-8 z-10"
        style={{ boxShadow: '0 25px 60px rgba(0,0,0,0.3)' }}
      >
        {/* Branding */}
        <div className="flex flex-col items-center mb-8">
          <img src={jobLogo} alt="Logo" className="h-16 mb-4 cursor-pointer" onClick={() => navigate("/")} />
          <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Forgot Password</h1>
          <p className="text-gray-500 text-sm mt-1 text-center">Enter your registered email and we&apos;ll send you a reset link</p>
        </div>

        {/* Success Message */}
        {message && (
          <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-3 mb-4 text-center">
            {message}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-4 py-3 mb-4 text-center">
            {error}
          </div>
        )}

        <Formik
          initialValues={{ email: "" }}
          validationSchema={validationSchema}
          onSubmit={handleForgotPassword}
        >
          {({ isSubmitting }) => (
            <Form>
              {/* Email */}
              <div className="mb-6">
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                  Email Address
                </label>
                <Field
                  type="email"
                  name="email"
                  placeholder="Enter your registered email"
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-800 text-sm placeholder-gray-400 outline-none transition-all duration-200 focus:border-blue-500 focus:bg-white focus:ring-2 focus:ring-blue-100"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-xs mt-1.5 ml-1" />
              </div>

              {/* Submit */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 text-white font-semibold text-sm rounded-xl border-none cursor-pointer transition-all duration-300 flex items-center justify-center gap-2 hover:shadow-lg disabled:opacity-60 disabled:cursor-not-allowed"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                }}
              >
                <Mail size={18} />
                {isSubmitting ? "Sending..." : "Send Reset Link"}
              </button>
            </Form>
          )}
        </Formik>

        {/* Back to Login */}
        <div className="mt-6 text-center">
          <span className="text-sm text-gray-500">
            Remember your password?{" "}
            <span
              className="text-blue-600 font-semibold cursor-pointer hover:text-blue-700 transition-colors"
              onClick={() => navigate("/login")}
            >
              Back to Sign In
            </span>
          </span>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
